// Direct browser fetch transport with dependency-injected fetch.
// No DOM, no cookies, no proxy knowledge.

import { normalizeErrorPreviewText } from "./fetch-primitives.ts";
import type { TileResponse } from "./types.ts";
import { ERROR_CODES } from "./types.ts";

export interface DirectTransportOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface DirectTransport {
  fetchResource(url: string, opts?: DirectTransportOptions): Promise<TileResponse>;
}

export type FetchImpl = (
  input: string,
  init?: Record<string, unknown>,
) => Promise<{
  url?: string;
  status: number;
  headers?: unknown;
  arrayBuffer(): Promise<ArrayBuffer>;
}>;

function hasCredentialHeader(headers: Record<string, string> | undefined): string | null {
  if (!headers) return null;
  for (const key of Object.keys(headers)) {
    const lower = key.toLowerCase();
    if (lower === "cookie" || lower === "authorization" || lower === "proxy-authorization") {
      return key;
    }
  }
  return null;
}

function toSafeHeaders(input: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input) return out;
  try {
    const h = input as {
      forEach?: (cb: (v: string, k: string) => void) => void;
      entries?: () => Iterable<[string, string]>;
    } & Record<string, string>;
    if (typeof h.forEach === "function") {
      h.forEach((v: string, k: string) => {
        out[String(k).toLowerCase()] = String(v);
      });
      return out;
    }
    if (typeof h.entries === "function") {
      for (const [k, v] of h.entries()) out[String(k).toLowerCase()] = String(v);
      return out;
    }
    if (typeof input === "object") {
      for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
        out[String(k).toLowerCase()] = String(v);
      }
    }
  } catch {
    // fall through with empty headers
  }
  return out;
}

const ERROR_PREVIEW_MAX_BYTES = 4096;
const ERROR_PREVIEW_MAX_CHARS = 300;

/**
 * Best-effort server signal from an HTTP error body: decode a bounded
 * prefix, strip markup, collapse to one line, truncate. Never throws;
 * returns "" when nothing usable remains. Quoted server content passes
 * through otherwise unchanged.
 */
export function extractErrorSignal(bytes: Uint8Array): string {
  const slice =
    bytes.length > ERROR_PREVIEW_MAX_BYTES ? bytes.subarray(0, ERROR_PREVIEW_MAX_BYTES) : bytes;
  let text = "";
  try {
    text = new TextDecoder("utf-8", { fatal: false }).decode(slice);
  } catch {
    return "";
  }
  // Early binary guard: error pages are text.
  if (text.includes("\0")) return "";
  return normalizeErrorPreviewText(text, ERROR_PREVIEW_MAX_CHARS);
}

async function errorPreview(response: {
  headers?: unknown;
  arrayBuffer(): Promise<ArrayBuffer>;
}): Promise<{ preview: string } | Record<string, never>> {
  try {
    const headers = toSafeHeaders(response.headers);
    const declared = Number(headers["content-length"]);
    if (Number.isSafeInteger(declared) && declared > 16 * 1024) return {};
    const bytes = new Uint8Array(await response.arrayBuffer());
    const preview = extractErrorSignal(bytes);
    return preview ? { preview } : {};
  } catch {
    // Preview is diagnostic only; the http-error outcome stands without it.
    return {};
  }
}

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  const e = err as { name?: string; code?: number } | null;
  if (e && e.name === "AbortError") return true;
  return false;
}

export function createDirectTransport(fetchImpl: FetchImpl): DirectTransport {
  async function fetchResource(url: string, opts?: DirectTransportOptions): Promise<TileResponse> {
    const badHeader = hasCredentialHeader(opts?.headers);
    if (badHeader) {
      return {
        outcome: "policy-denied",
        reason: `credential-header-rejected:${badHeader.toLowerCase()}`,
        code: ERROR_CODES.TRANSPORT_POLICY_DENIED,
      };
    }
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return {
        outcome: "policy-denied",
        reason: "invalid-url",
        code: ERROR_CODES.INVALID_INPUT,
      };
    }
    if (parsed.username !== "" || parsed.password !== "") {
      return {
        outcome: "policy-denied",
        reason: "url-userinfo-rejected",
        code: ERROR_CODES.TRANSPORT_POLICY_DENIED,
      };
    }
    if (opts?.signal?.aborted) {
      return { outcome: "cancelled", reason: "aborted-before-fetch" };
    }
    let response: {
      url?: string;
      status: number;
      headers?: unknown;
      arrayBuffer(): Promise<ArrayBuffer>;
    };
    try {
      response = await fetchImpl(url, {
        redirect: "follow",
        credentials: "omit",
        signal: opts?.signal,
        headers: opts?.headers ? { ...opts.headers } : undefined,
      });
    } catch (err: unknown) {
      if (opts?.signal?.aborted || isAbortError(err)) {
        return { outcome: "cancelled", reason: "aborted" };
      }
      const reason = err instanceof Error ? err.message : "fetch-rejected";
      return { outcome: "network-error", reason };
    }
    if (opts?.signal?.aborted) {
      return { outcome: "cancelled", reason: "aborted-after-response" };
    }
    const finalUrl = typeof response.url === "string" && response.url !== "" ? response.url : url;
    const status = response.status;
    const headers = toSafeHeaders(response.headers);
    // Do not infer CORS failure from status: a rejected fetch has no status.
    // Any resolved response is classified by status only.
    if (status >= 200 && status <= 299) {
      try {
        const bytes = await response.arrayBuffer();
        if (opts?.signal?.aborted) {
          return { outcome: "cancelled", reason: "aborted-during-body" };
        }
        return { outcome: "readable", finalUrl, status, headers, bytes };
      } catch (err: unknown) {
        if (opts?.signal?.aborted || isAbortError(err)) {
          return { outcome: "cancelled", reason: "aborted-during-body" };
        }
        const reason = err instanceof Error ? err.message : "body-read-failed";
        return { outcome: "network-error", reason };
      }
    }
    return { outcome: "http-error", finalUrl, status, headers, ...(await errorPreview(response)) };
  }

  return { fetchResource };
}
